/**
 * Service API pour les statistiques du dashboard
 * Utilise unifiedApi pour l'authentification automatique
 */

import unifiedApi from './unifiedApi.js';
import logger from '@/utils/logger';

/**
 * Service principal pour les données du dashboard
 */
export const dashboardApiService = {
  /**
   * Récupère les statistiques générales du dashboard
   */
  getOverviewStats: async () => {
    try {
      logger.debug('📊 DashboardApi - Récupération stats overview via unifiedApi');
      const data = await unifiedApi.dashboard.getOverviewStats();
      return data.data;
    } catch (error) {
      logger.error('Erreur API getOverviewStats:', error);
      throw error;
    }
  },

  /**
   * Récupère les statistiques détaillées des étudiants
   */
  getStudentStats: async () => {
    try {
      logger.debug('📊 DashboardApi - Récupération stats students via unifiedApi');
      const data = await unifiedApi.dashboard.getStudentStats();
      return data.data;
    } catch (error) {
      logger.error('Erreur API getStudentStats:', error);
      throw error;
    }
  },

  /**
   * Récupère les statistiques détaillées des parents
   */
  getParentStats: async () => {
    try {
      logger.debug('📊 DashboardApi - Récupération stats parents via unifiedApi');
      const data = await unifiedApi.dashboard.getParentStats();
      return data.data;
    } catch (error) {
      logger.error('Erreur API getParentStats:', error);
      throw error;
    }
  },

  /**
   * Récupère les activités récentes du système
   */
  getRecentActivities: async (limit = 10) => {
    try {
      logger.debug('📊 DashboardApi - Récupération activités récentes via unifiedApi');
      const data = await unifiedApi.dashboard.getRecentActivities(limit);
      return data.data;
    } catch (error) {
      logger.error('Erreur API getRecentActivities:', error);
      throw error;
    }
  },

  /**
   * Récupère l'état actuel des bus
   */
  getBusStatus: async () => {
    try {
      logger.debug('📊 DashboardApi - Récupération bus status via unifiedApi');
      const data = await unifiedApi.dashboard.getBusStatus();
      return data.data;
    } catch (error) {
      logger.error('Erreur API getBusStatus:', error);
      throw error;
    }
  },

  /**
   * Récupère les stats de délivrabilité push (T11)
   */
  getNotificationStats: async () => {
    try {
      const data = await unifiedApi.dashboard.getNotificationStats();
      return data?.data ?? null;
    } catch (error) {
      logger.warn('Stats notifications non disponibles:', error.message);
      return null;
    }
  },

  /**
   * Récupère les KPIs flotte avancés (trajets/jour 30j, durée moyenne, arrêts desservis/skippés)
   */
  getFleetStats: async () => {
    try {
      const data = await unifiedApi.dashboard.getFleetStats();
      return data?.data ?? null;
    } catch (error) {
      logger.warn('Stats flotte non disponibles:', error.message);
      return null;
    }
  },

  /**
   * Récupère la liste des parents avec statut d'activation + dernière connexion
   */
  getParentsDetail: async () => {
    try {
      const data = await unifiedApi.dashboard.getParentsDetail();
      return data?.data ?? null;
    } catch (error) {
      logger.warn('Détail parents non disponible:', error.message);
      return null;
    }
  },

  /**
   * Récupère la timeline notifications envoyées/délivrées/ouvertes par jour (30j)
   */
  getNotificationsTimeline: async () => {
    try {
      const data = await unifiedApi.dashboard.getNotificationsTimeline();
      return data?.data ?? null;
    } catch (error) {
      logger.warn('Timeline notifications non disponible:', error.message);
      return null;
    }
  },

  /**
   * Récupère toutes les données du dashboard en une seule fois
   */
  getAllDashboardData: async () => {
    try {
      const [overviewStats, studentStats, parentStats, recentActivities, busStatus, notificationStats] =
        await Promise.all([
          dashboardApiService.getOverviewStats(),
          dashboardApiService.getStudentStats(),
          dashboardApiService.getParentStats(),
          dashboardApiService.getRecentActivities(8),
          dashboardApiService.getBusStatus(),
          dashboardApiService.getNotificationStats(),
        ]);

      return {
        overview: overviewStats,
        students: studentStats,
        parents: parentStats,
        activities: recentActivities,
        buses: busStatus,
        notifications: notificationStats,
        lastUpdated: new Date().toISOString(),
      };
    } catch (error) {
      logger.error('Erreur API getAllDashboardData:', error);
      throw error;
    }
  },
};

/**
 * Fonctions utilitaires pour le formatage des données
 */
export const dashboardUtils = {
  /**
   * Formate les statistiques pour l'affichage dans les cartes
   */
  formatStatsForCards: (stats) => {
    if (!stats) return [];

    const activeBuses = stats.activeBuses ?? stats.buses?.active ?? 0;
    const totalBuses = stats.totalBuses ?? stats.buses?.total ?? 0;
    const activeStudents = stats.activeStudents ?? stats.students?.transported ?? 0;
    const totalStudents = stats.totalStudents ?? stats.students?.total ?? 0;
    const totalParents = stats.totalParents ?? stats.parents?.total ?? 0;
    const openIncidents = stats.open_incidents ?? stats.incidents?.today ?? 0;
    const weeklyInc = stats.weekly_incident_count ?? stats.incidents?.thisWeek ?? 0;

    return [
      {
        title: 'Bus actifs',
        value: activeBuses.toString(),
        change: '0',
        icon: 'Bus',
        color: 'blue',
        total: totalBuses,
      },
      {
        title: 'Élèves transportés',
        value: activeStudents.toString(),
        change: '0',
        icon: 'Users',
        color: 'green',
        total: totalStudents,
      },
      {
        title: 'Parents inscrits',
        value: totalParents.toString(),
        change: '0',
        icon: 'UserCheck',
        color: 'purple',
        total: totalParents,
      },
      {
        title: 'Incidents signalés',
        value: openIncidents.toString(),
        change: '0',
        icon: 'AlertTriangle',
        color: 'red',
        total: weeklyInc,
      },
    ];
  },

  /**
   * Formate les activités récentes pour l'affichage
   */
  formatActivitiesForDisplay: (activities) => {
    if (!activities || !Array.isArray(activities)) return [];

    return activities.map((activity) => ({
      id: activity.id,
      type: activity.type,
      category: activity.category,
      message: activity.message,
      time: activity.time,
      timestamp: activity.timestamp,
      icon: dashboardUtils.getActivityIcon(activity.category),
    }));
  },

  /**
   * Détermine l'icône appropriée pour une activité
   */
  getActivityIcon: (category) => {
    const iconMap = {
      student: 'UserPlus',
      parent: 'Users',
      bus: 'Bus',
      route: 'MapPin',
      driver: 'User',
      incident: 'AlertTriangle',
      maintenance: 'Tool',
      default: 'Info',
    };

    return iconMap[category] || iconMap.default;
  },

  /**
   * Obtient la couleur appropriée pour un statut de bus
   */
  getBusStatusColor: (status) => {
    const colorMap = {
      'En route': 'bg-green-100 text-green-800',
      "À l'arrêt": 'bg-yellow-100 text-yellow-800',
      Maintenance: 'bg-red-100 text-red-800',
      Garage: 'bg-gray-100 text-gray-800',
      default: 'bg-gray-100 text-gray-800',
    };

    return colorMap[status] || colorMap.default;
  },

  /**
   * Obtient la couleur appropriée pour un type d'activité
   */
  getActivityColor: (type) => {
    const colorMap = {
      success: 'text-green-600',
      warning: 'text-yellow-600',
      error: 'text-red-600',
      info: 'text-blue-600',
      default: 'text-gray-600',
    };

    return colorMap[type] || colorMap.default;
  },

  /**
   * Calcule le pourcentage de remplissage d'un bus
   */
  calculateBusOccupancy: (students, capacity) => {
    if (!capacity || capacity === 0) return 0;
    return Math.round((students / capacity) * 100);
  },

  /**
   * Formate une date pour l'affichage
   */
  formatDate: (dateString) => {
    if (!dateString) return '';

    const date = new Date(dateString);
    const now = new Date();
    const diffInHours = (now - date) / (1000 * 60 * 60);

    if (diffInHours < 1) {
      const diffInMinutes = Math.floor((now - date) / (1000 * 60));
      return `Il y a ${diffInMinutes} min`;
    } else if (diffInHours < 24) {
      return `Il y a ${Math.floor(diffInHours)}h`;
    } else {
      return date.toLocaleDateString('fr-FR', {
        day: 'numeric',
        month: 'short',
        hour: '2-digit',
        minute: '2-digit',
      });
    }
  },

  /**
   * Calcule la santé globale du système
   */
  calculateSystemHealth: (stats) => {
    if (!stats) return 0;

    const totalBuses = stats.totalBuses ?? stats.buses?.total ?? 0;
    const activeBuses = stats.activeBuses ?? stats.buses?.active ?? 0;
    const totalRoutes = stats.totalRoutes ?? stats.routes?.total ?? 0;
    const activeRoutes = stats.activeRoutes ?? stats.routes?.active ?? 0;
    const openIncidents = stats.open_incidents ?? stats.incidents?.today ?? 0;

    const busHealth = totalBuses ? (activeBuses / totalBuses) * 100 : 0;
    const routeHealth = totalRoutes ? (activeRoutes / totalRoutes) * 100 : 0;
    const incidentImpact = Math.max(0, 100 - openIncidents * 10);

    return Math.round((busHealth + routeHealth + incidentImpact) / 3);
  },
};

export default dashboardApiService;
